/**
 * Client for the arbor-v1 language-detection endpoint (PLAT-10899), used by the
 * translation-memory import to verify the chosen source/target languages match
 * the file's content (PLAT-10900). Kept as a standalone module (not on
 * `TranslationRepository`) so it stays a small, independently-testable IO unit.
 */

const DETECT_LANGUAGE_ENDPOINT = '/api/aiprompts/translations-detect-language';

export interface LanguageSampleSet {
  /** Caller id, echoed back on the matching result. */
  id: string;
  samples: string[];
}

export interface LanguageDetectionResult {
  id: string;
  /** Detected BCP-47 code, or "und" when undetermined. */
  language: string;
  confidence?: number;
  /** One-sentence justification for the detected language, when provided. */
  reason?: string;
}

/**
 * Detect the dominant language of each sample set. Mirrors the plain-`fetch`,
 * no-auth-header pattern of the sibling `/api/aiprompts/*` calls in
 * `TranslationRepository`. Throws on a non-OK response.
 */
export async function detectLanguages(
  sets: LanguageSampleSet[],
  signal?: AbortSignal,
): Promise<LanguageDetectionResult[]> {
  const base = process.env.REACT_APP_API_URL ?? '';
  const response = await fetch(`${base}${DETECT_LANGUAGE_ENDPOINT}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sets }),
    signal,
  });
  if (!response.ok) {
    throw new Error(`Language detection failed (${response.status}).`);
  }
  const data: { results?: LanguageDetectionResult[] } = await response.json();
  return data.results ?? [];
}
